/**
 * Seller Cart SDK
 *
 * SDK methods for the multi-seller cart architecture.
 * Each customer can have one active cart per seller.
 *
 * @see {@link file://../../api/docs/architecture/cart.md} for architecture details
 */
import type { Client, ClientHeaders } from "@medusajs/js-sdk";
import type { HttpTypes } from "@medusajs/types";
import type { SalonStockerHttpTypes } from "@repo/api-sdk";

// ============================================================
// SDK Class
// ============================================================

/**
 * SalonStocker Seller Cart SDK for the multi-seller cart architecture.
 *
 * In this architecture:
 * - Each customer can have one active cart per seller
 * - Adding items automatically routes to the correct seller's cart
 * - Standard Medusa endpoints work for individual cart operations
 *
 * @example
 * ```ts
 * // List all active carts for the customer
 * const { carts, summary } = await sdk.store.sellerCart.list();
 *
 * // Add item to a seller cart (creates cart if needed)
 * const result = await sdk.store.sellerCart.addItem({
 *   offer_id: "soff_123",
 *   quantity: 2
 * });
 * ```
 */
export class SellerCart {
  private client: Client;

  constructor(client: Client) {
    this.client = client;
  }

  /**
   * @deprecated
   */
  create = (
    _body: HttpTypes.StoreCreateCart,
    _query?: HttpTypes.SelectParams,
    _headers?: ClientHeaders
  ): Promise<HttpTypes.StoreCartResponse> =>
    Promise.reject(new Error("Deprecated"));
  /**
   * This method updates a cart. It sends a request to the
   * [Update Cart](https://docs.medusajs.com/api/store#carts_postcartsid) API route.
   *
   * Related guide: [How to update a cart in the storefront](https://docs.medusajs.com/resources/storefront-development/cart/update).
   *
   * @param id - The cart's ID.
   * @param body - The data to update in the cart.
   * @param query - Configure the fields to retrieve in the cart.
   * @param headers - Headers to pass in the request.
   * @returns The updated cart.
   *
   * @example
   * sdk.store.sellerCart.update("cart_123", {
   *   region_id: "reg_123"
   * })
   * .then(({ cart }) => {
   *   console.log(cart)
   * })
   */
  update = async (
    id: string,
    body: HttpTypes.StoreUpdateCart,
    query?: HttpTypes.SelectParams,
    headers?: ClientHeaders
  ) => {
    return this.client.fetch<HttpTypes.StoreCartResponse>(
      `/store/carts/seller-carts/${id}`,
      {
        method: "POST",
        headers,
        body,
        query
      }
    );
  };
  /**
   * This method retrieves a cart by its ID. It sends a request to the
   * [Get Cart](https://docs.medusajs.com/api/store#carts_getcartsid) API route.
   *
   * Related guide: [How to retrieve a cart in the storefront](https://docs.medusajs.com/resources/storefront-development/cart/retrieve).
   *
   * @param id - The cart's ID.
   * @param query - Configure the fields to retrieve in the cart.
   * @param headers - Headers to pass in the request.
   * @returns The cart's details.
   *
   * @example
   * To retrieve a cart by its ID:
   *
   * ```ts
   * sdk.store.sellerCart.retrieve("cart_123")
   * .then(({ cart }) => {
   *   console.log(cart)
   * })
   * ```
   *
   * To specify the fields and relations to retrieve:
   *
   * ```ts
   * sdk.store.sellerCart.retrieve("cart_123", {
   *   fields: "id,*items"
   * })
   * .then(({ cart }) => {
   *   console.log(cart)
   * })
   * ```
   *
   * Learn more about the `fields` property in the [API reference](https://docs.medusajs.com/api/store#select-fields-and-relations).
   */
  retrieve = async (
    id: string,
    query?: HttpTypes.SelectParams,
    headers?: ClientHeaders
  ) => {
    return this.client.fetch<HttpTypes.StoreCartResponse>(
      `/store/carts/seller-carts/${id}`,
      {
        headers,
        query
      }
    );
  };
  /**
   * @deprecated
   */
  createLineItem = (
    _cartId: string,
    _body: HttpTypes.StoreAddCartLineItem,
    _query?: HttpTypes.SelectParams,
    _headers?: ClientHeaders
  ): Promise<never> => Promise.reject(new Error("Deprecated"));
  /**
   * This method updates a line item in a seller cart using offer-based endpoint.
   * Unlike the standard line-items endpoint, this works with offer-based pricing
   * where variants don't have direct prices.
   *
   * @param cartId - The cart's ID.
   * @param lineItemId - The line item's ID.
   * @param body - The data to update (quantity only).
   * @param query - Configure the fields to retrieve in the cart.
   * @param headers - Headers to pass in the request.
   * @returns The cart's details.
   *
   * @example
   * sdk.store.sellerCart.updateLineItem(
   *   "cart_123",
   *   "cali_123",
   *   { quantity: 3 }
   * )
   * .then(({ cart }) => {
   *   console.log(cart)
   * })
   */
  updateLineItem = async (
    cartId: string,
    lineItemId: string,
    body: SalonStockerHttpTypes.StoreUpdateOfferLineItem,
    query?: HttpTypes.SelectParams,
    headers?: ClientHeaders
  ) => {
    return this.client.fetch<HttpTypes.StoreCartResponse>(
      `/store/carts/seller-carts/${cartId}/items/${lineItemId}`,
      {
        method: "POST",
        headers,
        body,
        query
      }
    );
  };
  /**
   * This method deletes a line item from a seller cart using offer-based endpoint.
   * Unlike the standard line-items endpoint, this works with offer-based pricing
   * where variants don't have direct prices.
   *
   * @param cartId - The cart's ID.
   * @param lineItemId - The item's ID.
   * @param headers - Headers to pass in the request.
   * @returns The deletion's details.
   *
   * @example
   * sdk.store.sellerCart.deleteLineItem(
   *   "cart_123",
   *   "cali_123"
   * )
   * .then(({ deleted, parent: cart }) => {
   *   console.log(deleted, cart)
   * })
   */
  deleteLineItem = async (
    cartId: string,
    lineItemId: string,
    headers?: ClientHeaders
  ) => {
    return this.client.fetch<HttpTypes.StoreLineItemDeleteResponse>(
      `/store/carts/seller-carts/${cartId}/items/${lineItemId}`,
      {
        method: "DELETE",
        headers
      }
    );
  };
  /**
   * Lists the variants a line item can be switched to, with the current one
   * flagged. For an offer line these are the other offers the same supplier
   * has for the product (identified by `offer_id`); for an ad hoc line they are
   * the supplier's catalog variants (identified by `shopify_variant_id`, with
   * `available: false` marking one that cannot be chosen).
   * Only meaningful for a line that reports `variant_change_available`.
   *
   * @param cartId - The cart's ID.
   * @param lineItemId - The line item's ID.
   * @param headers - Headers to pass in the request.
   * @returns The variant options, current one included.
   *
   * @example
   * sdk.store.sellerCart.getLineItemVariantOptions("cart_123", "cali_123")
   * .then(({ variant_options }) => {
   *   console.log(variant_options)
   * })
   */
  getLineItemVariantOptions = async (
    cartId: string,
    lineItemId: string,
    headers?: ClientHeaders
  ): Promise<SalonStockerHttpTypes.StoreVariantOptionsResponse> => {
    return this.client.fetch<SalonStockerHttpTypes.StoreVariantOptionsResponse>(
      `/store/carts/seller-carts/${cartId}/items/${lineItemId}/variant-options`,
      {
        method: "GET",
        headers
      }
    );
  };
  /**
   * Switches a line item to another variant of the same product from the same
   * supplier. If the target variant is already a line in the cart the backend
   * merges the quantities and removes the old line, so the returned cart can
   * have one line fewer.
   *
   * @param cartId - The cart's ID.
   * @param lineItemId - The line item's ID.
   * @param body - The variant to switch to: `{ offer_id }` for an offer line,
   * `{ shopify_variant_id }` for an ad hoc line. Exactly one key; never a price.
   * @param query - Configure the fields to retrieve in the cart.
   * @param headers - Headers to pass in the request.
   * @returns The refreshed cart.
   *
   * @example
   * sdk.store.sellerCart.changeLineItemVariant(
   *   "cart_123",
   *   "cali_123",
   *   { offer_id: "soff_456" }
   * )
   * .then(({ cart }) => {
   *   console.log(cart)
   * })
   */
  changeLineItemVariant = async (
    cartId: string,
    lineItemId: string,
    body: SalonStockerHttpTypes.StoreChangeLineItemVariant,
    query?: HttpTypes.SelectParams,
    headers?: ClientHeaders
  ) => {
    return this.client.fetch<SalonStockerHttpTypes.StoreChangeLineItemVariantResponse>(
      `/store/carts/seller-carts/${cartId}/items/${lineItemId}/change-variant`,
      {
        method: "POST",
        headers,
        body,
        query
      }
    );
  };
  /**
   * This method adds a shipping method to a cart. It sends a request to
   * the [Add Shipping Method](https://docs.medusajs.com/api/store#carts_postcartsidshippingmethods) API routes.
   *
   * Related guide: [Implement shipping step during checkout](https://docs.medusajs.com/resources/storefront-development/checkout/shipping).
   *
   * @param cartId - The cart's ID.
   * @param body - The shipping method's details.
   * @param query - Configure the fields to retrieve in the cart.
   * @param headers - Headers to pass in the request.
   * @returns The cart's details.
   *
   * @example
   * sdk.store.sellerCart.addShippingMethod("cart_123", {
   *   option_id: "so_123",
   *   data: {
   *     // custom data for fulfillment provider.
   *   }
   * })
   * .then(({ cart }) => {
   *   console.log(cart)
   * })
   */
  addShippingMethod = async (
    cartId: string,
    body: HttpTypes.StoreAddCartShippingMethods,
    query?: HttpTypes.SelectParams,
    headers?: ClientHeaders
  ) => {
    return this.client.fetch<HttpTypes.StoreCartResponse>(
      `/store/carts/seller-carts/${cartId}/shipping-methods`,
      {
        method: "POST",
        headers,
        body,
        query
      }
    );
  };
  /**
   * This method completes a cart and places the order. It's the last step of the checkout flow.
   * The method sends a request to the [Complete Cart](https://docs.medusajs.com/api/store#carts_postcartsidcomplete)
   * API route.
   *
   * Related guide: [Learn how to complete cart in checkout flow](https://docs.medusajs.com/resources/storefront-development/checkout/complete-cart).
   *
   * @param cartId - The cart's ID.
   * @param body - `order_set_id` groups a multi-cart checkout's orders;
   *   `payment_session_id` names the payment session the caller confirmed
   *   against, which the backend settles or refuses (KAN-56).
   * @param query - Configure the fields to retrieve in the created order.
   * @param headers - Headers to pass in the request.
   * @returns The order's details, if it was placed successfully. Otherwise, the cart is returned with an error.
   *
   * @example
   * sdk.store.sellerCart.complete("cart_123")
   * .then((data) => {
   *   if(data.type === "cart") {
   *     // an error occurred
   *     console.log(data.error, data.cart)
   *   } else {
   *     // order placed successfully
   *     console.log(data.order)
   *   }
   * })
   */
  complete = async (
    cartId: string,
    body?: { order_set_id?: string; payment_session_id?: string },
    query?: HttpTypes.SelectParams,
    headers?: ClientHeaders
  ) => {
    return this.client.fetch<HttpTypes.StoreCompleteCartResponse>(
      `/store/carts/seller-carts/${cartId}/complete`,
      {
        method: "POST",
        headers,
        body,
        query
      }
    );
  };

  /**
   * This method completes a cart and places the order. It's the last step of the checkout flow.
   * The method sends a request to the [Complete Cart](https://docs.medusajs.com/api/store#carts_postcartsidcomplete)
   * API route.
   *
   * Related guide: [Learn how to complete cart in checkout flow](https://docs.medusajs.com/resources/storefront-development/checkout/complete-cart).
   *
   * @param cartId - The cart's ID.
   * @param query - Configure the fields to retrieve in the created order.
   * @param headers - Headers to pass in the request.
   * @returns The order's details, if it was placed successfully. Otherwise, the cart is returned with an error.
   *
   * @example
   * sdk.store.sellerCart.complete("cart_123")
   * .then((data) => {
   *   if(data.type === "cart") {
   *     // an error occurred
   *     console.log(data.error, data.cart)
   *   } else {
   *     // order placed successfully
   *     console.log(data.order)
   *   }
   * })
   */
  /**
   * @deprecated
   */
  transferCart = (
    _id: string,
    _query?: HttpTypes.SelectParams,
    _headers?: ClientHeaders
  ): Promise<HttpTypes.StoreCartResponse> =>
    Promise.reject(new Error("Deprecated"));

  /**
   * Lists all active carts for the authenticated customer.
   * Each cart belongs to a different seller.
   *
   * @param headers - Optional headers to pass in the request
   * @returns All active carts with seller info and summary totals
   *
   * @example
   * ```ts
   * sdk.store.sellerCart.list()
   *   .then(({ carts, summary }) => {
   *     console.log(`You have ${summary.total_sellers} carts`);
   *     console.log(`Total: ${summary.grand_total}`);
   *     carts.forEach(cart => {
   *       console.log(`${cart.seller.name}: ${cart.items.length} items`);
   *     });
   *   })
   * ```
   */
  list = async (
    query?: SalonStockerHttpTypes.StoreSellerCartsFilters,
    headers?: ClientHeaders
  ): Promise<SalonStockerHttpTypes.StoreSellerCartsResponse> => {
    return await this.client.fetch<SalonStockerHttpTypes.StoreSellerCartsResponse>(
      "/store/carts/seller-carts",
      {
        method: "GET",
        headers,
        query
      }
    );
  };

  /**
   * Builds the external checkout URL for a seller cart - a redirect to the
   * seller's own store (external checkout) rather than the internal Stripe
   * flow. Returns the URL, the platform label (e.g. "shopify") for accurate
   * copy, and any line items that could not be mapped to the seller's platform.
   *
   * Only valid for sellers where `seller.supports_external_checkout` is true;
   * otherwise the request fails with a 409 and the caller should fall back to
   * the internal checkout.
   *
   * @param cartId - The seller cart ID
   * @param headers - Optional headers to pass in the request
   * @returns The external checkout URL, unmapped items, and platform
   *
   * @example
   * ```ts
   * const { url, unmapped, platform } =
   *   await sdk.store.sellerCart.externalCheckout("cart_123");
   * window.location.href = url;
   * ```
   */
  externalCheckout = async (
    cartId: string,
    headers?: ClientHeaders
  ): Promise<SalonStockerHttpTypes.StoreExternalCheckoutResponse> => {
    return await this.client.fetch<SalonStockerHttpTypes.StoreExternalCheckoutResponse>(
      `/store/carts/seller-carts/${cartId}/external-checkout`,
      {
        method: "GET",
        headers
      }
    );
  };

  /**
   * Confirms the external checkout handoff for a seller cart - marks the cart as
   * handed off (stamps `handed_off_at`) and emits the handoff event. Call this
   * only when the customer actually confirms the redirect to the seller's store,
   * so merely opening the interstitial (which fetches the URL via
   * {@link externalCheckout}) does not mark the cart as sent.
   *
   * @param cartId - The seller cart ID
   * @param headers - Optional headers to pass in the request
   * @returns Confirmation with the platform and mapped item count
   *
   * @example
   * ```ts
   * await sdk.store.sellerCart.confirmExternalCheckout("cart_123");
   * window.location.href = url;
   * ```
   */
  confirmExternalCheckout = async (
    cartId: string,
    headers?: ClientHeaders
  ): Promise<SalonStockerHttpTypes.StoreExternalCheckoutHandoffResponse> => {
    return await this.client.fetch<SalonStockerHttpTypes.StoreExternalCheckoutHandoffResponse>(
      `/store/carts/seller-carts/${cartId}/external-checkout`,
      {
        method: "POST",
        headers
      }
    );
  };

  /**
   * Adds an item to the seller's cart. If no cart exists for that seller,
   * a new cart is automatically created.
   *
   * The seller is determined from the offer_id - items are automatically
   * routed to the correct seller's cart.
   *
   * @param data - The item to add
   * @param headers - Optional headers to pass in the request
   * @returns The cart ID, seller ID, and whether a new cart was created
   *
   * @example
   * ```ts
   * sdk.store.sellerCart.addItem({
   *   offer_id: "soff_123",
   *   quantity: 2,
   *   region_id: "reg_456", // Only needed if creating new cart
   *   sales_channel_id: "sc_789" // Only needed if creating new cart
   * })
   * .then(({ cart_id, is_new_cart, seller_id }) => {
   *   console.log(`Added to cart ${cart_id} (new: ${is_new_cart})`);
   * })
   * ```
   */
  addItem = async (
    data: SalonStockerHttpTypes.StoreAddToSellerCart,
    headers?: ClientHeaders
  ): Promise<SalonStockerHttpTypes.StoreAddToSellerCartResponse> => {
    return await this.client.fetch<SalonStockerHttpTypes.StoreAddToSellerCartResponse>(
      "/store/carts/seller-carts",
      {
        method: "POST",
        body: data,
        headers
      }
    );
  };

  /**
   * Adds an ad hoc supplier item to a seller cart (KAN-193) - a hand-typed,
   * fixed-price line Caroline (the concierge operator) captured directly from
   * a supplier's own Shopify storefront via the browser extension's paste-in
   * flow, rather than a catalogued `SellerOffer`. A repeat add of the same
   * `(seller_id, shopify_variant_id)` merges into the existing line
   * (increments its quantity) instead of creating a second one - see
   * `AdHocLineIdentity` in `@salonstocker/framework`.
   *
   * Requires the concierge operator's capability token that authorizes this
   * call. Pass it via `headers`, mirroring {@link addItem} - never add it to
   * the SDK's global headers (`packages/api-sdk/src/index.ts`), so it only
   * ever reaches the calls that actually need it.
   *
   * @param data - The captured item, the chosen seller, and the (possibly
   *   Caroline-edited) unit price and quantity.
   * @param headers - Headers to pass in the request - must include
   *   `x-concierge-capability-token` for the call to succeed.
   * @returns The full updated seller cart, including the added/merged line.
   *
   * @example
   * ```ts
   * sdk.store.sellerCart.addAdHocItem(
   *   {
   *     seller_id: "sel_123",
   *     shopify_variant_id: "40287744385",
   *     shopify_product_id: "8123456789",
   *     title: "Blonde Toner 500ml",
   *     unit_price: 42.5,
   *     quantity: 1
   *   },
   *   { "x-concierge-capability-token": token }
   * ).then(({ cart }) => {
   *   console.log(cart)
   * })
   * ```
   */
  addAdHocItem = async (
    data: SalonStockerHttpTypes.StoreAddAdHocLineItem,
    headers?: ClientHeaders
  ): Promise<SalonStockerHttpTypes.StoreAddAdHocLineResponse> => {
    return await this.client.fetch<SalonStockerHttpTypes.StoreAddAdHocLineResponse>(
      "/store/carts/seller-carts/ad-hoc-items",
      {
        method: "POST",
        body: data,
        headers
      }
    );
  };

  /**
   * Looks up a suggested seller for the ad hoc add form's always-present
   * seller dropdown (KAN-193), by normalized host match against the
   * captured page's hosts. A pre-population convenience only - a miss or an
   * ambiguous match never fails the call, it just means
   * `suggested_seller_id` comes back `null` and nothing gets pre-selected;
   * Caroline can always choose a seller herself.
   *
   * Requires the concierge operator's capability token, same as
   * {@link addAdHocItem}.
   *
   * @param query - The captured page's hosts (`browsing_host`, and
   *   `canonical_myshopify_host` when the theme exposed it).
   * @param headers - Headers to pass in the request - must include
   *   `x-concierge-capability-token` for the call to succeed.
   * @returns Every onboarded Shopify seller (for the dropdown), plus the
   *   suggested seller id, if any.
   *
   * @example
   * ```ts
   * sdk.store.sellerCart.suggestAdHocSellers(
   *   { browsing_host: "bellezza.com.au" },
   *   { "x-concierge-capability-token": token }
   * ).then(({ sellers, suggested_seller_id }) => {
   *   console.log(sellers, suggested_seller_id)
   * })
   * ```
   */
  suggestAdHocSellers = async (
    query: SalonStockerHttpTypes.StoreSuggestAdHocSellerQuery,
    headers?: ClientHeaders
  ): Promise<SalonStockerHttpTypes.StoreAdHocSellerSuggestionResponse> => {
    return await this.client.fetch<SalonStockerHttpTypes.StoreAdHocSellerSuggestionResponse>(
      "/store/carts/seller-carts/ad-hoc-sellers",
      {
        method: "GET",
        headers,
        query
      }
    );
  };

  /**
   * This method adds a promotion to a cart. It sends a request to
   * the [Add Promotions to Cart Method](https://docs.medusajs.com/api/store#carts_postcartsidpromotions) API routes.
   *
   * Related guide: [Manage Cart Promotions in Storefront](https://docs.medusajs.com/resources/storefront-development/cart/manage-promotions).
   *
   * @param cartId - The cart's ID.
   * @param body - The data to add the promotions to the cart.
   * @param headers - Headers to pass in the request.
   * @returns The cart's details.
   *
   * @example
   * sdk.store.sellerCart.addPromotions("cart_123", {
   *   promo_codes: ["PROMO_CODE_1", "PROMO_CODE_2"],
   * })
   * .then(({ cart }) => {
   *   console.log(cart)
   * })
   */
  async addPromotions(
    id: string,
    body: HttpTypes.StoreCartAddPromotion,
    headers?: ClientHeaders
  ) {
    return await this.client.fetch<HttpTypes.StoreCartResponse>(
      `/store/carts/${id}/promotions`,
      {
        method: "POST",
        body,
        headers
      }
    );
  }

  /**
   * This method removes promotions from a cart. It sends a request to
   * the [Remove Promotions from Cart Method](https://docs.medusajs.com/api/store#carts_deletecartsidpromotions) API routes.
   *
   * @param cartId - The cart's ID.
   * @param body - The data to remove the promotions from the cart.
   * @param headers - Headers to pass in the request.
   * @returns The cart's details.
   *
   * @example
   * sdk.store.sellerCart.removePromotions("cart_123", {
   *   promo_codes: ["PROMO_CODE_1", "PROMO_CODE_2"],
   * })
   * .then(({ cart }) => {
   *   console.log(cart)
   * })
   */
  async removePromotions(
    id: string,
    body: HttpTypes.StoreCartRemovePromotion,
    headers?: ClientHeaders
  ) {
    return await this.client.fetch<HttpTypes.StoreCartResponse>(
      `/store/carts/${id}/promotions`,
      {
        method: "DELETE",
        body,
        headers
      }
    );
  }
}
